/** Pro-Tag-Zeitplan für Rechnungsseminare (Landing + Admin). */

export interface SeminarScheduleDay {
  id: string;
  /** ISO YYYY-MM-DD */
  date: string;
  /** HH:MM */
  timeFrom: string;
  /** HH:MM */
  timeTo: string;
}

export const createEmptyScheduleDay = (partial?: Partial<SeminarScheduleDay>): SeminarScheduleDay => ({
  id: `d_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  date: '',
  timeFrom: '09:00',
  timeTo: '17:00',
  ...partial,
});

export const normalizeScheduleDays = (raw: unknown): SeminarScheduleDay[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any, index: number) => ({
      id: String(item?.id || `d_${index}`),
      date: String(item?.date || '').trim(),
      timeFrom: String(item?.timeFrom || item?.from || '').trim(),
      timeTo: String(item?.timeTo || item?.to || '').trim(),
    }))
    .filter((d) => d.date);
};

export const sortScheduleDays = (days: SeminarScheduleDay[]): SeminarScheduleDay[] =>
  [...days].sort((a, b) => String(a.date).localeCompare(String(b.date)));

export const formatGermanWeekday = (isoDate: string): string => {
  const d = new Date(`${isoDate}T12:00:00`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { weekday: 'long' });
};

export const formatGermanDateShort = (isoDate: string): string => {
  const d = new Date(`${isoDate}T12:00:00`);
  if (isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const formatTimeRange = (from?: string, to?: string): string => {
  const f = String(from || '').trim();
  const t = String(to || '').trim();
  if (f && t) return `${f}–${t} Uhr`;
  if (f) return `ab ${f} Uhr`;
  if (t) return `bis ${t} Uhr`;
  return '';
};

/** z.B. „Sonntag, 21.06.2026 · 09:00–17:00 Uhr“ */
export const formatScheduleDayLine = (day: SeminarScheduleDay): string => {
  const wd = formatGermanWeekday(day.date);
  const date = formatGermanDateShort(day.date);
  const time = formatTimeRange(day.timeFrom, day.timeTo);
  const head = [wd, date].filter(Boolean).join(', ');
  return time ? `${head} · ${time}` : head;
};

/** Kompakt für Listen/Archiv: „21. & 22.06.2026“ */
export const buildDateSummaryFromDays = (days: SeminarScheduleDay[]): string => {
  const sorted = sortScheduleDays(normalizeScheduleDays(days));
  if (!sorted.length) return '';
  if (sorted.length === 1) return formatGermanDateShort(sorted[0].date);

  const monthKeys = sorted.map((d) => d.date.slice(0, 7));
  const sameMonth = monthKeys.every((k) => k === monthKeys[0]);
  if (sameMonth && sorted.length <= 3) {
    const dayNums = sorted.map((d) => new Date(`${d.date}T12:00:00`).getDate());
    const last = formatGermanDateShort(sorted[sorted.length - 1].date);
    const monthYear = last.replace(/^\d+\./, ''); // ".06.2026"
    return `${dayNums.join('. & ')}${monthYear.startsWith('.') ? monthYear : `.${monthYear}`}`;
  }
  return sorted.map((d) => formatGermanDateShort(d.date)).join(' & ');
};

/** Freitext-Datum für Archiv/Filter — bevorzugt strukturierte Tage. */
export const resolveSeminarDateText = (event: {
  date?: string | null;
  scheduleDays?: SeminarScheduleDay[] | null;
}): string => {
  const fromDays = buildDateSummaryFromDays(event.scheduleDays || []);
  if (fromDays) return fromDays;
  return String(event.date || '').trim();
};
